/**
 * พนักงานอ่านสัญญาและลงลายมือชื่อ (มือถือเป็นหลัก)
 *
 * ลำดับที่บังคับ — ห้ามข้าม เพราะเป็นสิ่งที่ทำให้ลายเซ็นมีน้ำหนักทางกฎหมาย:
 *   อ่านสัญญาทั้งฉบับ → ติ๊กรับทราบ → วาดลายเซ็น → ขอรหัส OTP ทางไลน์ → ยืนยัน
 *
 * ⭐ ปุ่ม "ลงนาม" ต้องกดไม่ได้จนกว่าจะครบทุกขั้น และต้องบอกด้วยว่า **ขาดขั้นไหน**
 * ไม่ใช่ปุ่มเทาเฉย ๆ ให้คนเดาเอง
 */
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ShieldCheck, Eraser, Download, CheckCircle2 } from 'lucide-react';
import { PageLayout } from '../components/layout/PageLayout';
import { Loading } from '../components/layout/Loading';
import { LoadError } from '../components/layout/LoadError';
import { useToast } from '../components/ui/toast';
import {
  useMyContract, useRequestContractOtp, useSignContract,
  type ContractSectionView,
} from '../hooks';
import { apiDownload } from '../lib/api-client';

const BLANK = '...............';

function txt(v: unknown): string {
  const s = typeof v === 'string' ? v.trim() : typeof v === 'number' ? String(v) : '';
  return s || BLANK;
}

function thaiLongDate(iso: unknown): string {
  if (typeof iso !== 'string' || !iso) return BLANK;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return BLANK;
  const months = ['มกราคม','กุมภาพันธ์','มีนาคม','เมษายน','พฤษภาคม','มิถุนายน','กรกฎาคม','สิงหาคม','กันยายน','ตุลาคม','พฤศจิกายน','ธันวาคม'];
  // เวลาไทยก่อนอ่านค่า — ให้ตรงกับที่พิมพ์ลง PDF ฝั่งเซิร์ฟเวอร์เป๊ะ ๆ
  const th = new Date(d.getTime() + 7 * 3600_000);
  return `${th.getUTCDate()} ${months[th.getUTCMonth()]} ${th.getUTCFullYear() + 543}`;
}

function baht(v: unknown): string {
  const n = Number(v);
  return Number.isFinite(n) ? n.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : BLANK;
}

/** ช่องลายเซ็น — รองรับทั้งนิ้วบนมือถือและเมาส์บนคอม */
function SignaturePad({ onChange }: { onChange: (dataUrl: string | null) => void }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  const dirty = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // ความคมชัดบนจอ retina — ถ้าไม่คูณ dpr ลายเซ็นจะเบลอเป็นขั้นบันได
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = Math.round(rect.width * dpr);
    canvas.height = Math.round(rect.height * dpr);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#111827';
  }, []);

  function pos(e: React.PointerEvent<HTMLCanvasElement>) {
    const rect = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  function start(e: React.PointerEvent<HTMLCanvasElement>) {
    e.currentTarget.setPointerCapture(e.pointerId);
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    drawing.current = true;
    const { x, y } = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  }

  function move(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawing.current) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const { x, y } = pos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    dirty.current = true;
  }

  function end() {
    if (!drawing.current) return;
    drawing.current = false;
    const canvas = canvasRef.current;
    if (!canvas) return;
    onChange(dirty.current ? canvas.toDataURL('image/png') : null);
  }

  function clear() {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    dirty.current = false;
    onChange(null);
  }

  return (
    <div className="space-y-2">
      <canvas
        ref={canvasRef}
        // touch-none = กันหน้าจอเลื่อนตามนิ้วขณะวาด (ไม่ใส่แล้วเซ็นบนมือถือไม่ได้เลย)
        className="w-full h-40 border-2 border-dashed border-gray-300 rounded-lg bg-white touch-none"
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerLeave={end}
        onPointerCancel={end}
      />
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-500">วาดลายเซ็นด้วยนิ้วในกรอบด้านบน</span>
        <button type="button" className="btn-secondary btn-slim" onClick={clear}>
          <Eraser className="w-3.5 h-3.5" /> ล้างลายเซ็น
        </button>
      </div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2 py-1 text-sm border-b border-dashed border-gray-200 last:border-0">
      <div className="w-32 shrink-0 text-gray-600">{label}</div>
      <div className="flex-1 min-w-0 break-words">{value}</div>
    </div>
  );
}

export function ContractSign() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error, refetch } = useMyContract(id);
  const requestOtp = useRequestContractOtp();
  const sign = useSignContract();
  const toast = useToast();

  const [acknowledged, setAcknowledged] = useState(false);
  const [signature, setSignature] = useState<string | null>(null);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');

  if (isLoading) return <PageLayout title="สัญญาจ้าง"><Loading /></PageLayout>;
  if (error || !data) {
    // 404 ที่นี่ = "ไม่ใช่สัญญาของคุณ" หรือ "ยังไม่ถูกส่งมาให้เซ็น" — ต้องบอก
    // เป็นภาษาคนว่าให้ทำอะไรต่อ ไม่ใช่ "โหลดข้อมูลไม่สำเร็จ" ลอย ๆ ซึ่งชวนให้
    // กดลองใหม่ซ้ำ ๆ ทั้งที่กดกี่ครั้งก็ไม่มีวันขึ้น
    const status =
      typeof error === 'object' && error !== null && 'status' in error
        ? Number((error as { status: unknown }).status)
        : 0;
    return (
      <PageLayout title="สัญญาจ้าง">
        <LoadError
          error={error}
          hint={
            status === 404
              ? 'ไม่พบสัญญาฉบับนี้ในบัญชีของคุณ — อาจเป็นสัญญาของพนักงานคนอื่น หรือฝ่ายบุคคลยังไม่ได้ส่งมาให้ลงนาม กรุณาสอบถามฝ่ายบุคคล'
              : undefined
          }
          onRetry={() => void refetch()}
        />
      </PageLayout>
    );
  }

  const c = data.contract;
  const f = c.fields as Record<string, unknown>;
  const already = c.status === 'signed';
  const cancelled = c.status === 'cancelled';

  async function handleRequestOtp() {
    if (!id) return;
    try {
      await requestOtp.mutateAsync(id);
      setOtpSent(true);
      toast.success('ส่งรหัสยืนยัน 6 หลักเข้าไลน์ของคุณแล้ว (หมดอายุใน 10 นาที)');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ขอรหัสยืนยันไม่สำเร็จ');
    }
  }

  async function handleSign() {
    if (!id || !signature) return;
    try {
      await sign.mutateAsync({ id, otp: otp.trim(), signature_data_url: signature, acknowledged: true });
      toast.success('ลงนามเรียบร้อยแล้ว — ดาวน์โหลดสัญญาเก็บไว้ได้เลย');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ลงนามไม่สำเร็จ');
    }
  }

  async function download() {
    if (!id) return;
    try {
      await apiDownload(`/me/contracts/${encodeURIComponent(id)}/pdf`, `สัญญาจ้าง-${c.doc_no.replace('/', '-')}.pdf`);
    } catch (e) {
      toast.error(`ดาวน์โหลดไม่สำเร็จ: ${e instanceof Error ? e.message : 'เกิดข้อผิดพลาด'}`);
    }
  }

  // บอกให้ชัดว่ายังขาดขั้นไหน — ห้ามปล่อยปุ่มเทาเฉย ๆ ให้คนเดา
  const missing: string[] = [];
  if (!acknowledged) missing.push('ติ๊กยืนยันว่าอ่านสัญญาแล้ว');
  if (!signature) missing.push('วาดลายเซ็น');
  if (!/^\d{6}$/.test(otp.trim())) missing.push('กรอกรหัสยืนยัน 6 หลัก');

  return (
    <PageLayout title="สัญญาจ้าง">
      <div className="space-y-4 pb-8">
        <div className="card p-4">
          <div className="font-semibold">{data.company.name || 'สัญญาจ้างพนักงาน'}</div>
          <div className="text-sm text-gray-500">
            เลขที่ {c.doc_no} · ทำ ณ วันที่ {thaiLongDate(c.issued_at)}
          </div>
          {/* ⭐ ต้องเปิดฉบับเต็มได้ "ก่อน" เซ็น — คนเซ็นเอกสารที่ตัวเองยังไม่เคย
              เห็นทั้งฉบับ เป็นข้อโต้แย้งที่ทำให้ลายเซ็นเสียน้ำหนักทันที */}
          {!cancelled && (
            <button className="btn-secondary btn-slim mt-3" onClick={() => void download()}>
              <Download className="w-3.5 h-3.5" /> เปิดสัญญาฉบับเต็ม (PDF)
            </button>
          )}
          {already && (
            <div className="mt-3 flex items-center gap-2 text-green-700 text-sm">
              <CheckCircle2 className="w-5 h-5 shrink-0" />
              ลงนามเรียบร้อยแล้ว
            </div>
          )}
          {cancelled && (
            <div className="mt-3 text-sm text-red-700">
              สัญญาฉบับนี้ถูกยกเลิกแล้ว — กรุณาติดต่อฝ่ายบุคคล
            </div>
          )}
        </div>

        <div className="card p-4">
          <div className="font-semibold mb-2">ส่วนที่ 1 · รายละเอียดการจ้างงาน</div>
          <Field label="ชื่อ-นามสกุล" value={txt(f.empNameTh)} />
          <Field label="ตำแหน่ง" value={txt(f.position)} />
          <Field label="ฝ่าย / แผนก" value={txt(f.department)} />
          <Field label="สถานที่ทำงาน" value={txt(f.workplace)} />
          <Field label="วันเริ่มสัญญา" value={thaiLongDate(f.startDate)} />
          <Field label="วันสิ้นสุดสัญญา" value={f.endDate ? thaiLongDate(f.endDate) : BLANK} />
          <Field label="ช่วงทดลองงาน" value={f.probationDays ? `${txt(f.probationDays)} วัน` : BLANK} />
          <Field label="อัตราค่าจ้าง" value={`${baht(f.salaryAmount)} บาท ${txt(f.salaryUnit ?? f.payCycle)}`} />
          <Field label="วันจ่ายค่าจ้าง" value={txt(f.paydayText)} />
          <Field label="วิธีการจ่าย" value={txt(f.payMethod)} />
          {Array.isArray(f.duties) && (f.duties as string[]).length > 0 && (
            <div className="mt-2">
              <div className="text-sm text-gray-600 mb-1">หน้าที่ความรับผิดชอบ</div>
              <ol className="list-decimal pl-5 text-sm space-y-0.5">
                {(f.duties as string[]).map((d, i) => <li key={i}>{d}</li>)}
              </ol>
            </div>
          )}
        </div>

        <div className="card p-4">
          <div className="font-semibold mb-1">ส่วนที่ 2 · ข้อตกลงและเงื่อนไขการจ้างงาน</div>
          <p className="text-xs text-gray-500 mb-3">กรุณาอ่านให้ครบทุกข้อก่อนลงนาม</p>
          <div className="space-y-4">
            {(c.sections as ContractSectionView[]).map((s) => (
              <div key={s.heading}>
                <div className="font-semibold text-sm mb-1.5">{s.heading}</div>
                <div className="space-y-2.5">
                  {s.clauses.map((cl) => (
                    <div key={cl.no}>
                      <div className="text-sm font-medium">ข้อ {cl.no} {cl.title}</div>
                      {cl.paragraphs.map((p, i) => (
                        <p key={i} className="text-sm text-gray-700 mt-0.5 text-justify">{p}</p>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {already ? (
          <div className="card p-4 space-y-3">
            <p className="text-sm text-gray-600">
              เก็บไฟล์สัญญาไว้เป็นหลักฐานได้เลย ไฟล์นี้มีรหัสตรวจสอบเอกสารกำกับไว้
              หากมีการแก้ไขภายหลัง รหัสจะไม่ตรงกัน
            </p>
            <button className="btn-cta w-full py-3" onClick={() => void download()}>
              <Download className="w-5 h-5" /> ดาวน์โหลดสัญญา (PDF)
            </button>
          </div>
        ) : cancelled ? null : (
          <div className="card p-4 space-y-4">
            <div className="font-semibold">ลงลายมือชื่อ</div>

            <label className="flex items-start gap-2.5 text-sm">
              <input
                type="checkbox"
                className="mt-0.5 w-5 h-5 shrink-0"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
              />
              <span>
                ข้าพเจ้าได้อ่านและเข้าใจข้อความในสัญญาฉบับนี้โดยละเอียดแล้ว
                และตกลงผูกพันตามข้อตกลงทั้งหมด
              </span>
            </label>

            <SignaturePad onChange={setSignature} />

            <div>
              <label className="label">รหัสยืนยันทางไลน์</label>
              <div className="flex gap-2">
                <input
                  className="input flex-1 text-lg tracking-[0.3em]"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="______"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                />
                <button
                  className="btn-secondary whitespace-nowrap"
                  onClick={() => void handleRequestOtp()}
                  disabled={requestOtp.isPending}
                >
                  {requestOtp.isPending ? 'กำลังส่ง…' : otpSent ? 'ขอรหัสใหม่' : 'ขอรหัส'}
                </button>
              </div>
              {otpSent && (
                <p className="text-xs text-gray-500 mt-1">
                  ส่งรหัสเข้าไลน์แล้ว · รหัสมีอายุ 10 นาที · กรอกผิดเกิน 5 ครั้งต้องขอใหม่
                </p>
              )}
            </div>

            <button
              className="btn-cta w-full py-3"
              onClick={() => void handleSign()}
              disabled={sign.isPending || missing.length > 0}
            >
              <ShieldCheck className="w-5 h-5" />
              {sign.isPending ? 'กำลังลงนาม…' : 'ลงนามสัญญา'}
            </button>
            {missing.length > 0 && (
              <p className="text-xs text-amber-700 text-center">
                ยังลงนามไม่ได้ — เหลือ: {missing.join(' · ')}
              </p>
            )}
            <p className="text-xs text-gray-500 text-center">
              การลงนามนี้มีผลผูกพันตามกฎหมาย ระบบจะบันทึกวันเวลา อุปกรณ์ และการยืนยันตัวตนไว้เป็นหลักฐาน
            </p>
          </div>
        )}
      </div>
    </PageLayout>
  );
}
